import { Controller } from "@hotwired/stimulus";
import StimulusReflex from "stimulus_reflex";

export default class extends Controller {
  static targets = ["input", "results", "selectedId", "newForm", "spinner"];
  static values = {
    appellationId: String,
    appellationName: String,
  };

  connect() {
    console.log("AppellationSearchController connected");
    StimulusReflex.register(this);

    // Add focus/blur listeners for spinner control
    this.inputTarget.addEventListener("focus", this.handleFocus.bind(this));
    this.inputTarget.addEventListener("blur", this.handleBlur.bind(this));

    // Add click outside listener
    document.addEventListener("click", this.clickOutside.bind(this));
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutside.bind(this));
  }

  handleFocus() {
    // Show spinner when input is focused and has content
    if (this.inputTarget.value.trim()) {
      this.showSpinner();
    }
  }

  handleBlur() {
    // Hide spinner when input loses focus
    setTimeout(() => {
      this.hideSpinner();
    }, 200); // Small delay to allow for click events on results
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden");
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden");
    }
  }

  search() {
    const query = this.inputTarget.value.trim();
    console.log("=== Appellation Search Debug ===");
    console.log("Search triggered with query:", query);
    console.log("Query length:", query.length);
    console.log("Element dataset:", this.element.dataset);
    console.log("Results target exists:", this.hasResultsTarget);
    console.log("Spinner target exists:", this.hasSpinnerTarget);

    if (!query) {
      console.log("Empty query, hiding results");
      this.resultsTarget.classList.add("hidden");
      this.resultsTarget.classList.remove("opacity-50");
      this.resultsTarget.innerHTML = "";
      this.hideSpinner();
      return;
    }

    if (query.length < 2) {
      console.log("Query too short (< 2 chars), showing 'enter more' message");
      this.showEnterMoreMessage();
      return;
    }

    // Show spinner and results container before search
    this.showSpinner();
    this.resultsTarget.classList.remove("hidden");
    this.resultsTarget.classList.add("opacity-50");
    this.element.setAttribute("data-query", query);

    try {
      console.log("Attempting to trigger AppellationSearch#search reflex");
      console.log("Dataset before reflex:", this.element.dataset);
      this.stimulate("AppellationSearch#search");
      console.log("Reflex triggered successfully");
    } catch (error) {
      console.error("Error triggering reflex:", error);
      console.error("Error details:", error.message);
      console.error("Error stack:", error.stack);
      this.resultsTarget.classList.add("hidden");
      this.resultsTarget.classList.remove("opacity-50");
      this.hideSpinner();
      this.showErrorMessage("Search failed. Please try again.");
    }
  }

  // Reflex lifecycle methods
  beforeSearch() {
    console.log("=== beforeSearch lifecycle method ===");
    console.log("Results target:", this.resultsTarget);
    this.resultsTarget.classList.add("opacity-50");
    this.showSpinner();
  }

  searchSuccess() {
    console.log("=== searchSuccess lifecycle method ===");
    console.log(
      "Results target content length:",
      this.resultsTarget.innerHTML.length
    );
    console.log(
      "Results target HTML:",
      this.resultsTarget.innerHTML.substring(0, 200) + "..."
    );

    this.resultsTarget.classList.remove("opacity-50");
    this.hideSpinner();

    const resultCount = this.resultsTarget.querySelectorAll(
      "[data-appellation-id]"
    ).length;
    console.log("Found appellation results:", resultCount);

    if (resultCount === 0) {
      console.log("No results found, showing 'no results' message");
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-2 text-sm text-gray-500">
          No results found. Please enter something.
        </div>
      `;
    } else {
      console.log("Results found, keeping results visible");
    }
  }

  searchError(error) {
    console.error("=== searchError lifecycle method ===");
    console.error("Error object:", error);
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);

    this.resultsTarget.classList.add("hidden");
    this.resultsTarget.classList.remove("opacity-50");
    this.hideSpinner();
    this.showErrorMessage("Search failed. Please try again.");
  }

  selectAppellation(event) {
    const appellationId = event.currentTarget.dataset.appellationId;
    const appellationName = event.currentTarget.dataset.appellationName;

    this.selectedIdTarget.value = appellationId;
    this.inputTarget.value = appellationName;
    this.appellationIdValue = appellationId;
    this.appellationNameValue = appellationName;
    this.resultsTarget.classList.add("hidden");
    this.resultsTarget.classList.remove("opacity-50");
    this.newFormTarget.classList.add("hidden");
    this.hideSpinner();
  }

  createNewAppellation(event) {
    event.preventDefault();
    event.stopPropagation();
    this.resultsTarget.classList.add("hidden");
    this.resultsTarget.classList.remove("opacity-50");
    this.newFormTarget.classList.remove("hidden");
    this.selectedIdTarget.value = "";
    this.hideSpinner();
  }

  // Hide results when clicking outside
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.resultsTarget.classList.add("hidden");
      this.resultsTarget.classList.remove("opacity-50");
      this.hideSpinner();
    }
  }

  showEnterMoreMessage() {
    console.log("Showing 'enter more' message");
    this.resultsTarget.classList.remove("hidden");
    this.resultsTarget.classList.remove("opacity-50");
    this.resultsTarget.innerHTML = `
      <div class="px-4 py-2 text-sm text-gray-500">
        No results found. Please enter something.
      </div>
    `;
    this.hideSpinner();
  }

  showErrorMessage(message) {
    console.log("Showing error message:", message);
    this.resultsTarget.classList.remove("hidden");
    this.resultsTarget.classList.remove("opacity-50");
    this.resultsTarget.innerHTML = `
      <div class="px-4 py-2 text-sm text-red-500">
        ${message}
      </div>
    `;
    this.hideSpinner();
  }
}
